import type { ResolvedApp } from "./index-types";
import { normalizePath } from "./StudioScreenRenderer.utils.path";
import { extractLocalePrefix } from "./StudioScreenRenderer.utils.locale-route";

function splitRouteSegments(input: string): string[] {
  return normalizePath(input).split("/").map((segment) => segment.trim()).filter(Boolean);
}

function isRouteParamSegment(segment: string): boolean {
  return /^\[[^/]+\]$/.test(segment) || /^:[^/]+$/.test(segment);
}

function routeSegmentMatches(template: string, actual: string): boolean {
  return isRouteParamSegment(template) ? actual.length > 0 : template === actual;
}

export function extractRouteParamsFromTemplate(
  template: string,
  actual: string,
): Record<string, string> | null {
  const templateSegments = splitRouteSegments(template);
  const actualSegments = splitRouteSegments(actual);

  if (templateSegments.length === actualSegments.length) {
    const params: Record<string, string> = {};
    for (let i = 0; i < templateSegments.length; i += 1) {
      const templateSegment = templateSegments[i];
      const actualSegment = actualSegments[i];
      if (!templateSegment || !actualSegment) return null;
      if (isRouteParamSegment(templateSegment)) {
        params[templateSegment.replace(/^\[|\]$/g, "").replace(/^:/, "")] = actualSegment;
      } else if (!routeSegmentMatches(templateSegment, actualSegment)) {
        return null;
      }
    }
    return params;
  }

  if (templateSegments.length === actualSegments.length + 1) {
    const lastTemplateSegment = templateSegments[templateSegments.length - 1];
    if (!lastTemplateSegment || !isRouteParamSegment(lastTemplateSegment)) return null;
    return templateSegments.slice(0, -1).every((segment, index) =>
      routeSegmentMatches(segment, actualSegments[index] as string),
    ) ? {} : null;
  }
  return null;
}

export function inferVirtualRouteParams(
  slug: string,
  pathname: string,
): Record<string, string> {
  const segments = splitRouteSegments(pathname);
  if (
    normalizePath(slug) === "tasks" &&
    segments[0] === "tasks" &&
    typeof segments[1] === "string" &&
    segments[1].trim()
  ) {
    return { status: segments[1].trim() };
  }
  return {};
}

export function deriveRouteParamsForScreen(
  app: ResolvedApp,
  slug: string,
  path: string,
): Record<string, string> {
  // The locale prefix is language metadata, not a route param — strip it
  // before matching the screen's route template.
  const strippedPath =
    extractLocalePrefix(path, app.i18n?.locales ?? [])?.strippedPath ??
    String(path ?? "");
  const template = app.config.routes?.[slug];
  if (template) {
    const extracted = extractRouteParamsFromTemplate(template, strippedPath);
    if (extracted) return extracted;
  }
  return inferVirtualRouteParams(slug, strippedPath);
}